import { LuMenu } from 'react-icons/lu';
import { GlobalSearch } from './GlobalSearch';
import { NotificationsMenu } from './NotificationsMenu';
import { UserMenu } from './UserMenu';
import { Breadcrumbs } from './Breadcrumbs';
import { useAuth } from '@/features/auth/AuthProvider';
import { ROLE_LABELS } from '@/features/auth/permissions';
import { useRealtimeSync } from '@/hooks/useRealtimeSync';
import { cn } from '@/lib/cn';

export function Topbar({ onOpenMobileNav }: { onOpenMobileNav: () => void }) {
  const { user, isDemo } = useAuth();
  const live = useRealtimeSync(!!user && !isDemo);
  return (
    <header className="sticky top-0 z-20 border-t-[3px] border-b border-t-accent-400 border-b-slate-200 bg-white/95 backdrop-blur">
      {isDemo && (
        <div className="bg-amber-50 px-4 py-1 text-center text-[11px] text-amber-900 ring-1 ring-amber-200">
          <strong>Demo mode</strong> — showing in-memory sample data. Configure Supabase in <code>.env</code> to use the live database.
        </div>
      )}
      <div className="flex h-14 items-center gap-3 px-4 lg:px-6">
        <button onClick={onOpenMobileNav} className="-ml-1 rounded-md p-2 text-slate-600 hover:bg-slate-100 lg:hidden" aria-label="Open menu">
          <LuMenu className="size-5" />
        </button>
        <GlobalSearch className="w-full max-w-xl" />
        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          {user && (
            <span className="hidden rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-800 ring-1 ring-inset ring-brand-200 xl:inline" title="Your role">
              Role: {ROLE_LABELS[user.role]}
            </span>
          )}
          {live !== 'off' && (
            <span
              className={cn(
                'hidden items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-medium sm:inline-flex',
                live === 'live' ? 'text-emerald-700' : live === 'error' ? 'text-red-700' : 'text-slate-500',
              )}
              title={live === 'live' ? 'Live: changes by other users appear automatically' : live === 'error' ? 'Live updates disconnected — refresh the page' : 'Connecting to live updates…'}
            >
              <span className={cn('size-2 rounded-full', live === 'live' ? 'animate-pulse bg-emerald-500' : live === 'error' ? 'bg-red-500' : 'bg-slate-400')} />
              {live === 'live' ? 'Live' : live === 'error' ? 'Offline' : 'Connecting'}
            </span>
          )}
          <NotificationsMenu />
          <UserMenu />
        </div>
      </div>
      <div className="flex h-9 items-center border-t border-slate-100 px-4 lg:px-6">
        <Breadcrumbs />
      </div>
    </header>
  );
}
